import { PathsV1AppRoleUuidGetParametersQueryExtend } from "@/api-schema-enums";
import { useAppRoleByUuidQuery } from "@/common/api/app-role/use-app-role-by-uuid.query";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { FormCard } from "@/components/ui/form-card";
import { Loader, LoaderSize } from "@/components/ui/loader";
import { AlertTriangle } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { AppRoleUpdateForm } from "./_components/app-role-update-form";

export function AppRoleUuidUpdateView() {
  const { t } = useTranslation(["ram", "common"]);
  const [_, navigate] = useLocation();
  const params = useParams<{ applicationUuid: string; appRoleUuid: string }>();

  const formId = useId();
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const {
    data: appRole,
    error: errorAppRole,
    isLoading: isLoadingAppRole,
  } = useAppRoleByUuidQuery(params.appRoleUuid, {
    parameters: {
      extend: [PathsV1AppRoleUuidGetParametersQueryExtend.Application],
    },
  });

  if (isLoadingAppRole || errorAppRole || !appRole) {
    return (
      <CenteredLayout
        variant={CenteredLayoutVariant.Table}
        title={t("ram:EditAppRole")}
        subTitle={t("common:ReviewAndSaveChangesDescription")}
      >
        <div className="flex min-h-[30vh] items-center justify-center">
          <Loader size={LoaderSize.Large} />
        </div>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("ram:EditAppRole")}
      subTitle={t("common:ReviewAndSaveChangesDescription")}
    >
      <div className="space-y-6">
        <div className="space-y-4">
          {appRole.v1Id !== null && appRole.v1Id !== undefined ? (
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t("ram:Attention")}</AlertTitle>
              <AlertDescription>{t("ram:V1ManagedAppRoleEditInfo")}</AlertDescription>
            </Alert>
          ) : null}

          <FormCard title={t("ram:EditAppRole")} titleHidden>
            <AppRoleUpdateForm
              formId={formId}
              appRole={appRole}
              applicationUuid={params.applicationUuid}
              appRoleUuid={params.appRoleUuid}
              onSaveDisabledChange={setIsSaveDisabled}
            />
          </FormCard>
        </div>

        <div className="flex flex-wrap justify-end gap-4">
          <Button
            type="button"
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            onClick={() => navigate(`~/ram/application/${params.applicationUuid}/app-role`)}
          >
            {t("common:Cancel")}
          </Button>
          <Button type="submit" form={formId} disabled={isSaveDisabled}>
            {t("common:Save")}
          </Button>
        </div>
      </div>
    </CenteredLayout>
  );
}
